import React from 'react';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { ExportJurisdiction } from 'api/ExportHubAPI';
import StyledSingleSelect from './StyledSingleSelect';

/**
 * Jurisdiction conditional detail fields (spec §4.6). One config per regime,
 * driving both the Add Product wizard's Jurisdiction step and the product-detail
 * Jurisdiction Details panel. Values are stored as a flat string map in the
 * product's `jurisdiction_details` jsonb. Standalone ECCN/USML no longer live in
 * generic Product Details — they belong here.
 */

export type JurFieldType = 'text' | 'textarea' | 'select';

export interface JurField {
  key: string;
  label: string;
  type: JurFieldType;
  options?: string[];
  placeholder?: string;
  hint?: string;
  optional?: boolean;
}

// USML categories I–XXI (spec §4.6 — ITAR).
// Shared with the licence wizard (DSP-5 Block 11) — one list, one source.
export const USML_CATEGORIES = [
  'I — Firearms & Close Assault Weapons',
  'II — Guns & Armament',
  'III — Ammunition & Ordnance',
  'IV — Launch Vehicles, Missiles, Rockets',
  'V — Explosives & Energetic Materials',
  'VI — Surface Vessels of War',
  'VII — Ground Vehicles',
  'VIII — Aircraft & Related Articles',
  'IX — Military Training Equipment',
  'X — Personal Protective Equipment',
  'XI — Military Electronics',
  'XII — Fire Control, Laser, Imaging',
  'XIII — Materials & Miscellaneous Articles',
  'XIV — Toxicological Agents',
  'XV — Spacecraft & Related Articles',
  'XVI — Nuclear Weapons Related Articles',
  'XVII — Classified Articles',
  'XVIII — Directed Energy Weapons',
  'XIX — Gas Turbine Engines',
  'XX — Submersible Vessels',
  'XXI — Articles Not Otherwise Enumerated',
];

const EAR_LICENSE_EXCEPTIONS = [
  'NLR — No License Required',
  'LVS — Limited Value Shipments',
  'GBS — Group B Shipments',
  'TSR — Technology & Software Restricted',
  'ENC — Encryption Commodities',
  'TMP — Temporary Imports/Exports',
  'RPL — Servicing & Replacement Parts',
  'STA — Strategic Trade Authorization',
  'GOV — Governments & International Orgs',
];

const NRC_MATERIAL_CATEGORIES = [
  'Category I — Strategic quantity',
  'Category II — Moderate quantity',
  'Category III — Low quantity',
  'Source Material',
  'Byproduct Material',
];

export const JUR_FIELD_CONFIG: Record<ExportJurisdiction, JurField[]> = {
  ITAR: [
    {
      key: 'usml_category',
      label: 'USML Category',
      type: 'select',
      options: USML_CATEGORIES,
    },
    {
      key: 'defense_article',
      label: 'Defense Article Description',
      type: 'textarea',
      placeholder: 'Describe the defense article or defense service…',
    },
    {
      key: 'empowered_official',
      label: 'Empowered Official',
      type: 'text',
      placeholder: 'Name of the empowered official',
    },
    {
      key: 'agreement_reference',
      label: 'MLA / TAA / WDA Reference',
      type: 'text',
      placeholder: 'e.g. TAA-2024-0142',
      optional: true,
    },
  ],
  EAR: [
    // ECCN is a first-class Product Detail (drives classification), not a
    // regime-only detail — see EditableProductDetails.
    {
      key: 'license_exception',
      label: 'License Exception',
      type: 'select',
      options: EAR_LICENSE_EXCEPTIONS,
    },
    {
      key: 'reason_for_control',
      label: 'Reason for Control',
      type: 'text',
      placeholder: 'e.g. NS, MT, RS',
      hint: 'NS, MT, NP, CB, RS, CC, AT, EI, FC, SS…',
    },
  ],
  NRC: [
    {
      key: 'cfr_part',
      label: '10 CFR Part Reference',
      type: 'text',
      placeholder: 'e.g. 10 CFR Part 110',
    },
    {
      key: 'material_category',
      label: 'Nuclear Material Category',
      type: 'select',
      options: NRC_MATERIAL_CATEGORIES,
    },
    {
      key: 'control_notes',
      label: 'Control Notes',
      type: 'textarea',
      placeholder: 'Additional control notes…',
      optional: true,
    },
  ],
  EAR99: [],
  // No regime determined yet, so there are no regime-specific details to
  // collect. Picking a real jurisdiction reveals that regime's fields.
  Unknown: [],
  'Not Subject': [
    {
      key: 'basis',
      label: 'Reason / Basis',
      type: 'textarea',
      placeholder:
        'Explain why this product is not subject to ITAR/EAR/NRC control…',
    },
  ],
};

// Explanatory note for regimes with no detail fields (spec §4.6).
export const JUR_NOTE: Partial<Record<ExportJurisdiction, string>> = {
  EAR99:
    'Subject to the EAR but no license generally required (EAR99). No additional jurisdiction detail is needed.',
  Unknown:
    'No export regime has been determined for this product yet. Set a jurisdiction — or run it through the Export Workflow — to record one.',
};

export const hasJurisdictionFields = (j: ExportJurisdiction): boolean =>
  (JUR_FIELD_CONFIG[j]?.length || 0) > 0;

/**
 * Every key any regime's form owns — the scope of a jurisdiction save's patch.
 *
 * The panel writes this whole set (values it holds, nulls for the rest) so a
 * regime switch still clears the old regime's detail, while keys outside the
 * set — an integration-written `eccn`, say — are never touched.
 */
export const ALL_JUR_FIELD_KEYS: string[] = Array.from(
  new Set(
    Object.keys(JUR_FIELD_CONFIG).flatMap((j) =>
      (JUR_FIELD_CONFIG[j as ExportJurisdiction] || []).map((f) => f.key),
    ),
  ),
);

export type JurDetails = Record<string, string>;

/** Read a string map safely out of the product's jurisdiction_details jsonb. */
export const toJurDetails = (raw: unknown): JurDetails => {
  if (!raw || typeof raw !== 'object') return {};
  const out: JurDetails = {};
  Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== null && v !== undefined) out[k] = String(v);
  });
  return out;
};

/** Label/value rows for read-only display (skips empty values). */
export const jurisdictionDetailRows = (
  j: ExportJurisdiction,
  details: JurDetails,
): Array<{ label: string; value: string }> =>
  (JUR_FIELD_CONFIG[j] || [])
    .map((f) => ({ label: f.label, value: (details[f.key] || '').trim() }))
    .filter((r) => r.value !== '');

interface FieldsProps {
  jurisdiction: ExportJurisdiction;
  values: JurDetails;
  onChange: (_next: JurDetails) => void;
}

/** Editable jurisdiction-detail fields, rendered from the per-regime config. */
export const JurisdictionDetailsFields: React.FC<FieldsProps> = ({
  jurisdiction,
  values,
  onChange,
}) => {
  const fields = JUR_FIELD_CONFIG[jurisdiction] || [];
  const note = JUR_NOTE[jurisdiction];
  const set = (key: string, val: string) =>
    onChange({ ...values, [key]: val });

  if (fields.length === 0) {
    return (
      <div className='eh-jur-note'>
        {note || 'No additional jurisdiction detail is required.'}
      </div>
    );
  }

  return (
    <>
      {fields.map((f) => (
        <div className='eh-field' key={f.key}>
          <span className='eh-field-label'>
            {f.label}
            {!f.optional && <span className='eh-req'> *</span>}
          </span>
          {f.type === 'select' ? (
            <StyledSingleSelect
              options={[
                { value: '', label: 'Select…' },
                ...(f.options || []).map((o) => ({ value: o, label: o })),
              ]}
              value={values[f.key] || ''}
              onChange={(v) => set(f.key, v)}
              inline
            />
          ) : f.type === 'textarea' ? (
            <textarea
              className='eh-input eh-textarea'
              value={values[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={f.placeholder}
            />
          ) : (
            <input
              className='eh-input'
              value={values[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              maxLength={300}
              placeholder={f.placeholder}
            />
          )}
          {f.hint && <span className='eh-field-hint'>{f.hint}</span>}
        </div>
      ))}

      {jurisdiction === 'Not Subject' && (
        <div className='eh-field'>
          <span className='eh-field-label'>Supporting Document</span>
          <button type='button' className='eh-upload-stub' disabled>
            <UploadFileOutlinedIcon style={{ fontSize: 16 }} />
            Upload supporting document
            <span className='export-hub-preview-pill'>Coming soon</span>
          </button>
        </div>
      )}
    </>
  );
};
